import { Grid, Stack, Typography } from "@mui/material";
import { omit } from "lodash";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import { useQuery } from "../../misc/useQuery";
import type { RewardResponse } from "../../rewards/rewardsApi";
import { useRewardWithId } from "../../rewards/useRewardWithId";
import type { Reward } from "./RewardEdit";
import RewardEdit from "./RewardEdit";
import { useCreateReward } from "./useCreateReward";

const newReward: () => Reward = () => ({
  name: "",
  description: "",
  cost: 0,
  images: null,
});

function clone(reward: RewardResponse): Reward {
  return omit({
    ...reward,
    name: `${reward.name} (kopia)`,
    description: reward.description,
    cost: reward.cost,
    image: reward.image,
  });
}

interface Props {
  readonly initialReward: Reward;
}

const NewRewardPage: FC<Props> = ({ initialReward }) => {
  const navigate = useNavigate();
  const { createReward } = useCreateReward();
  const [errors, setErrors] = useState<string[]>([]);
  const { control, handleSubmit, watch } = useForm<Reward>({
    defaultValues: initialReward,
  });

  useEffect(() => {
    const subscription = watch(() => setErrors((s) => (s.length === 0 ? s : [])));
    return () => subscription.unsubscribe();
  }, [watch]);

  const submitReward = async (state: Reward) => {
    const { id } = await createReward({
      name: state.name,
      description: state.description,
      cost: state.cost,
      image: state.image,
    });
    navigate(`/admin/rewards/${id}`);
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 1,
        marginBottom: 1,
        paddingLeft: 1,
        paddingRight: 1,
      }}
    >
      <RewardEdit control={control} errors={errors} />
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Stack direction="row" spacing={2}>
          <CancelButton onClick={() => navigate("/admin/rewards")} />
          <AsyncLoadingButton onClick={handleSubmit(submitReward)}>Zapisz</AsyncLoadingButton>
        </Stack>
      </Grid>
    </Grid>
  );
};

const InitializedNewRewardPage = () => {
  const query = useQuery();
  const [state, setState] = useState<Reward>();
  const fromPackageId = query.get("from");
  const { data: existingPackage, isLoading } = useRewardWithId(fromPackageId ?? undefined);
  useEffect(() => {
    if (!isLoading && fromPackageId && existingPackage) {
      setState(clone(existingPackage));
    }
    if (!fromPackageId) {
      setState(newReward());
    }
  }, [fromPackageId, existingPackage, isLoading]);

  if (!isLoading && !state) {
    return (
      <Grid container>
        <Grid size={12}>
          <Typography variant="h5">Taka nagroda nie istnieje</Typography>
        </Grid>
      </Grid>
    );
  }

  return <NewRewardPage initialReward={state!} />;
};

export default InitializedNewRewardPage;
